import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Headphones, Mail, MessageCircle, Copy, Check } from 'lucide-react';
import {
  SUPPORT_EMAIL,
  SUPPORT_EMAIL_2,
  SUPPORT_MAILTO,
  SUPPORT_MAILTO_2,
  SUPPORT_WHATSAPP_URL,
} from '../constants/contact';
import {
  buildLoginMailtoUrl,
  buildLoginWhatsAppUrl,
  copyTextToClipboard,
} from '../utils/loginErrorReport';
import { openExternalUrl } from '../utils/openExternalUrl';

const actionBtnClass =
  'group flex min-w-[4.75rem] flex-col items-center gap-1.5 rounded-xl p-2 transition-colors hover:bg-surface focus-visible:bg-surface focus-visible:outline-none sm:min-w-[6rem] sm:gap-2';

export function LoginContactSupport({ labels, reportText = '' }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const rootRef = useRef(null);
  const menuId = useId();
  const hasReport = Boolean(reportText && String(reportText).trim());

  const whatsappHref = useMemo(
    () => (hasReport ? buildLoginWhatsAppUrl(reportText) : SUPPORT_WHATSAPP_URL),
    [hasReport, reportText]
  );
  const emailHref = useMemo(
    () => (hasReport ? buildLoginMailtoUrl(reportText) : SUPPORT_MAILTO),
    [hasReport, reportText]
  );

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  const onCopyReport = async () => {
    if (!hasReport) return;
    const ok = await copyTextToClipboard(reportText);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div
      ref={rootRef}
      className="fixed z-50 flex flex-col items-end bottom-[max(1rem,var(--app-safe-bottom))] right-[max(1rem,env(safe-area-inset-right,0px))]"
    >
      <div
        id={menuId}
        role="menu"
        aria-label={labels.contactChoose}
        className={`absolute bottom-full right-0 z-50 mb-3 w-max max-w-[min(calc(100vw-2rem),18rem)] origin-bottom-right transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none sm:max-w-[20rem] ${
          open
            ? 'pointer-events-auto translate-y-0 scale-100 opacity-100'
            : 'pointer-events-none translate-y-2 scale-95 opacity-0'
        }`}
        aria-hidden={!open}
      >
        <div className="relative overflow-hidden rounded-2xl border border-border bg-card px-3 py-3.5 shadow-card ring-1 ring-black/5 dark:shadow-[0_8px_32px_rgba(0,0,0,0.45)] sm:px-5 sm:py-5">
          <p className="mb-2.5 text-center text-xs font-medium text-muted sm:mb-3 sm:text-sm">
            {hasReport
              ? labels.loginReportFabHint || 'Send your login error report'
              : labels.contactChoose}
          </p>
          <div className="flex items-start justify-center gap-1 sm:gap-4" role="none">
            <a
              href={emailHref}
              role="menuitem"
              onClick={(e) => {
                if (hasReport) {
                  e.preventDefault();
                  openExternalUrl(emailHref);
                }
                setOpen(false);
              }}
              className={actionBtnClass}
              aria-label={`${labels.contactEmail} ${SUPPORT_EMAIL}`}
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/15 transition-transform group-hover:scale-105 group-active:scale-95 sm:h-14 sm:w-14">
                <Mail className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden />
              </span>
              <span className="text-center text-[11px] font-medium leading-tight text-text sm:text-sm">
                {labels.contactEmail}
              </span>
            </a>

            {!hasReport && (
              <a
                href={SUPPORT_MAILTO_2}
                role="menuitem"
                onClick={() => setOpen(false)}
                className={actionBtnClass}
                aria-label={`${labels.contactEmail} ${SUPPORT_EMAIL_2}`}
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/15 transition-transform group-hover:scale-105 group-active:scale-95 sm:h-14 sm:w-14">
                  <Mail className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden />
                </span>
                <span className="text-center text-[11px] font-medium leading-tight text-text sm:text-sm max-w-[5.5rem] truncate sm:max-w-[6.5rem]">
                  {SUPPORT_EMAIL_2.split('@')[0]}
                </span>
              </a>
            )}

            {hasReport && (
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  void onCopyReport();
                }}
                className={actionBtnClass}
                aria-label={labels.loginReportCopy || 'Copy report'}
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/15 transition-transform group-hover:scale-105 group-active:scale-95 sm:h-14 sm:w-14">
                  {copied ? (
                    <Check className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden />
                  ) : (
                    <Copy className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden />
                  )}
                </span>
                <span className="text-center text-[11px] font-medium leading-tight text-text sm:text-sm">
                  {copied
                    ? labels.loginReportCopiedShort || 'Copied'
                    : labels.loginReportCopyShort || 'Copy'}
                </span>
              </button>
            )}

            <a
              href={whatsappHref}
              role="menuitem"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                if (hasReport) {
                  e.preventDefault();
                  openExternalUrl(whatsappHref);
                }
                setOpen(false);
              }}
              className={actionBtnClass}
              aria-label={labels.contactWhatsApp}
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#25D366]/15 text-[#128C7E] ring-1 ring-[#25D366]/25 transition-transform group-hover:scale-105 group-active:scale-95 dark:text-[#25D366] sm:h-14 sm:w-14">
                <MessageCircle className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden />
              </span>
              <span className="text-center text-[11px] font-medium leading-tight text-text sm:text-sm">
                {labels.contactWhatsApp}
              </span>
            </a>
          </div>
        </div>

        <div
          className="absolute bottom-0 right-5 h-3 w-3 translate-y-1/2 rotate-45 border border-border border-t-0 border-l-0 bg-card sm:right-6"
          aria-hidden
        />
      </div>

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={menuId}
        aria-haspopup="true"
        title={hasReport ? labels.loginReportTitle || labels.contactSupport : labels.contactSupport}
        className={`relative z-[51] inline-flex min-h-touch shrink-0 items-center justify-center gap-2 rounded-full bg-primary text-white shadow-lg transition-[transform,box-shadow,background-color] hover:bg-primary-hover hover:shadow-xl active:scale-[0.97] max-sm:h-14 max-sm:w-14 max-sm:p-0 sm:px-5 sm:py-3 ${
          open ? 'ring-2 ring-primary/40 ring-offset-2 ring-offset-surface' : ''
        } ${hasReport ? 'ring-2 ring-[#25D366]/50' : ''}`}
      >
        <Headphones className="h-5 w-5 shrink-0 sm:h-[1.125rem] sm:w-[1.125rem]" aria-hidden />
        <span className="hidden text-sm font-semibold sm:inline">
          {hasReport ? labels.loginReportFab || labels.contactSupport : labels.contactSupport}
        </span>
        <span className="sr-only sm:hidden">
          {hasReport ? labels.loginReportFab || labels.contactSupport : labels.contactSupport}
        </span>
        {hasReport && (
          <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full bg-[#25D366] ring-2 ring-surface" aria-hidden />
        )}
      </button>
    </div>
  );
}
